import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useEditDirectory } from '@repo/api/directory/useEditDirectory';
import { useEditFile } from '@repo/api/file/useEditFile';
import { getIsFSEntityFile } from '@repo/lib/fs/getIsFSEntityFile';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { ArrowRight, FileIcon, Folder } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the EditFSEntryName component.
 * @property {FilePublicWithPermissions | DirectoryPublic} [element] - The file or directory to edit.
 * @property {string} [directoryId] - Directory ID context for editing.
 * @property {(open: boolean) => void} onOpenChange - Callback for dialog open state.
 * @property {boolean} open - Whether the dialog is open.
 */
interface EditFSEntryNameProps {
  element?: FilePublicWithPermissions | DirectoryPublic;
  directoryId?: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

const EditFSEntryNameSchema = z.object({
  id: z.string(),
  name: z.string().min(2, 'Nazwa jest wymagana'),
});
type EditFSEntryNameSchemaType = z.infer<typeof EditFSEntryNameSchema>;

function useEditFSEntryForm(element?: FilePublicWithPermissions | DirectoryPublic, directoryId?: string) {
  const directoryMutation = useEditDirectory(directoryId);
  const fileMutation = useEditFile(directoryId);

  const isFile = getIsFSEntityFile(element);

  return {
    mutation: isFile ? fileMutation : directoryMutation,
    isFile,
  };
}

export default function EditFSEntryName(editFsEntryNameProps: EditFSEntryNameProps) {
  const { element, onOpenChange, open, directoryId } = editFsEntryNameProps;
  const { mutation: editMutation, isFile } = useEditFSEntryForm(element, directoryId);
  const { mutateAsync, isPending } = editMutation;

  const splitName = (fileName?: string) => {
    if (fileName === undefined) {
      return { name: '', extension: '' };
    }
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return { name: fileName, extension: '' };
    }
    return {
      name: fileName.substring(0, lastDotIndex),
      extension: fileName.substring(lastDotIndex),
    };
  };

  const form = useForm<EditFSEntryNameSchemaType>({
    mode: 'onBlur',
    defaultValues: {
      id: '',
      name: '',
    },
    resolver: standardSchemaResolver(EditFSEntryNameSchema),
  });

  const onClose = () => {
    form.reset();
    onOpenChange(false);
  };

  const onSubmit = (data: EditFSEntryNameSchemaType) => {
    const { extension } = splitName(element?.name);
    const newData = { ...data, name: data.name + extension };
    mutateAsync(newData)
      .then(() => {
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  useEffect(() => {
    if (open && element) {
      const { name } = splitName(element.name);
      form.reset({ id: element.id, name: name });
    }
  }, [open, element, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'sm:max-w-108'} onCloseAutoFocus={onClose} onEscapeKeyDown={onClose}>
        <DialogDescription className={'sr-only'}>Formularz do edycji pliku/folderu</DialogDescription>
        <Form {...form}>
          <DialogBody>
            <DialogHeader>
              <DialogTitle>Edytuj nazwę</DialogTitle>
            </DialogHeader>

            <form className={'flex grow flex-col gap-3 overflow-auto p-4'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
              <FormField
                name={'name'}
                control={form.control}
                render={({ field }) => (
                  <FormItem className={'col-span-1'}>
                    <FormControl>
                      <InputGroup>
                        <InputGroupInput {...field} placeholder={'Nazwa'} />
                        <InputGroupAddon align="inline-start">{isFile ? <FileIcon className="text-muted-foreground" /> : <Folder className="text-muted-foreground" />}</InputGroupAddon>
                      </InputGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </DialogBody>
        </Form>
        <DialogFooter>
          <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
            Anuluj
          </Button>
          <Button disabled={isPending} onClick={form.handleSubmit(onSubmit)} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
            <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
              Zapisz
              <ArrowRight className={'transition-transform group-hover:translate-x-0.5'} />
            </LoadingSwap>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
